import { Component, OnInit } from "@angular/core";
import { MatCheckboxChange } from "@angular/material";
import { List } from "linqts";
import { ApplicationType } from "../../../entity/ApplicationType";
import { Country } from "../../../entity/Country";
import { Organization } from "../../../entity/Organization";
import { TechnologyCategory } from "../../../entity/TechnologyCategory";
import { Typology } from "../../../entity/Typology";
import { ApplicationTypeService } from "../../services/ApplicationTypeService";
import { OrganizationService } from "../../services/OrganizationService";
import { TechnologyCategoryService } from "../../services/TechnologyCategoryService";

@Component({
	selector: "organizations-view",
	templateUrl: "./organizations.component.html",
	styleUrls: ["./organizations.component.scss"],
})
export class OrganizationsComponent implements OnInit {

	public modelView: {
		sideNavMode: string;
		sideNavOpened: boolean;
	} = {
		sideNavMode: "side",
		sideNavOpened: false,
	};

	public organizations: Organization[];
	public applicationTypes: Array<{ applicationType: ApplicationType, active: boolean }>;
	public countries: Array<{ value: Country, active: boolean }> = [
		{ value: Country.EU, active: true },
		{ value: Country.NON_EU, active: true },
	];
	public typologies: Array<{ typology: Typology, active: boolean }> = [
		{ typology: Typology.FUNDING, active: true },
		{ typology: Typology.INTERMEDIARY_ORG, active: true },
		{ typology: Typology.TECHNOLOGY_PROVIDER, active: true },
		{ typology: Typology.TECHNOLOGY_USER, active: true },
	];
	public technologyCategories: Array<{ value: TechnologyCategory, active: boolean }>;

	private readonly applicationTypeService: ApplicationTypeService;
	private readonly organizationService: OrganizationService;
	private readonly technologyCategoryService: TechnologyCategoryService;

	private constructor(applicationTypeService: ApplicationTypeService, organizationService: OrganizationService, technologyCategoryService: TechnologyCategoryService) {
		this.applicationTypeService = applicationTypeService;
		this.organizationService = organizationService;
		this.technologyCategoryService = technologyCategoryService;
	}

	public ngOnInit(): void {
		this.onWindowScreenResize();
		window.addEventListener("resize", this.onWindowScreenResize.bind(this));
		this.ngOnInitAsync().catch((reason: Error) => {
			console.error(reason);
		});
	}

	public async onApplicationTypeCheckboxChange($event: MatCheckboxChange): Promise<void> {
		const applicationType: ApplicationType = await this.applicationTypeService.getByIdAsync(parseInt($event.source.name, 10));
		if ($event.checked) {
			this.applicationTypes = new List(this.applicationTypes).Select((value) => {
				if (applicationType.id === value.applicationType.id) {
					value.active = true;
				}
				return value;
			}).ToArray();
		} else {
			this.applicationTypes = new List(this.applicationTypes).Select((value) => {
				if (applicationType.id === value.applicationType.id) {
					value.active = false;
				}
				return value;
			}).ToArray();
		}
		await this.updateOrganizations();
	}

	public async onCountryCheckboxChange($event: MatCheckboxChange): Promise<void> {
		let country: Country = null;
		switch ($event.source.name) {
			case Country.EU:
				country = Country.EU;
				break;
			case Country.NON_EU:
				country = Country.NON_EU;
				break;
		}
		if ($event.checked) {
			this.countries = new List(this.countries).Select((value) => {
				if (country === value.value) {
					value.active = true;
				}
				return value;
			}).ToArray();
		} else {
			this.countries = new List(this.countries).Select((value) => {
				if (country === value.value) {
					value.active = false;
				}
				return value;
			}).ToArray();
		}
		await this.updateOrganizations();
	}

	public async onTypologyCheckboxChange($event: MatCheckboxChange): Promise<void> {
		let typology: Typology = null;
		switch ($event.source.name) {
			case Typology.TECHNOLOGY_PROVIDER:
				typology = Typology.TECHNOLOGY_PROVIDER;
				break;
			case Typology.TECHNOLOGY_USER:
				typology = Typology.TECHNOLOGY_USER;
				break;
			case Typology.INTERMEDIARY_ORG:
				typology = Typology.INTERMEDIARY_ORG;
				break;
			case Typology.FUNDING:
				typology = Typology.FUNDING;
				break;
		}
		if ($event.checked) {
			this.typologies = new List(this.typologies).Select((value) => {
				if (typology === value.typology) {
					value.active = true;
				}
				return value;
			}).ToArray();
		} else {
			this.typologies = new List(this.typologies).Select((value) => {
				if (typology === value.typology) {
					value.active = false;
				}
				return value;
			}).ToArray();
		}
		await this.updateOrganizations();
	}

	public async onTechnologyCategoryCheckboxChange($event: MatCheckboxChange): Promise<void> {
		const technologyCategory: TechnologyCategory = await this.technologyCategoryService.getByIdAsync(parseInt($event.source.name, 10));
		if ($event.checked) {
			this.technologyCategories = new List(this.technologyCategories).Select((value) => {
				if (technologyCategory.id === value.value.id) {
					value.active = true;
				}
				return value;
			}).ToArray();
		} else {
			this.technologyCategories = new List(this.technologyCategories).Select((value) => {
				if (technologyCategory.id === value.value.id) {
					value.active = false;
				}
				return value;
			}).ToArray();
		}
		await this.updateOrganizations();
	}

	private async ngOnInitAsync(): Promise<void> {
		this.applicationTypes = new List(await this.applicationTypeService.getAllAsync()).Select((appType) => {
			return { applicationType: appType, active: true };
		}).ToArray();
		this.technologyCategories = new List(await this.technologyCategoryService.getAllAsync()).Select((technologyCategory) => {
			return { value: technologyCategory, active: true };
		}).ToArray();
		await this.updateOrganizations();
	}

	private onWindowScreenResize(): void {
		if (window.screen.width >= 1000) {
			this.modelView.sideNavMode = "side";
			this.modelView.sideNavOpened = true;
		} else {
			this.modelView.sideNavMode = "over";
			this.modelView.sideNavOpened = true;
		}
	}

	private async updateOrganizations(): Promise<void> {
		this.organizations = new List(await this.organizationService.getAllAsync()).Where((org) => {
			/* APPLICATION TYPE FILTER */
			let applicationTypes: ApplicationType[] = [];
			org.technologies.forEach((t) => t.applicationTypes.forEach((a) => applicationTypes.push(a)));
			applicationTypes = new List(applicationTypes).DistinctBy((key) => key.id).ToArray();
			return new List(applicationTypes).Select((a) => a.id).Intersect(new List(this.applicationTypes).Where((t) => t.active).Select((t) => t.applicationType.id)).ToArray().length !== 0;
			// return new List(applicationTypes).Intersect(new List(this.applicationTypes).Where((t) => t.active === true).Select((t) => t.applicationType)).ToArray().length !== 0;
		}).Where((org) => {
			/* Country FILTER */
			return new List(org.countries).Intersect(new List(this.countries).Where((t) => t.active === true).Select((t) => t.value)).ToArray().length !== 0;
		}).Where((org) => {
			/* TYPOLOGY FILTER */
			return new List(org.typology).Intersect(new List(this.typologies).Where((t) => t.active === true).Select((t) => t.typology)).ToArray().length !== 0;
		}).Where((org) => {
			/* TECHNOLOGY CATEGORY FILTER */
			let tecnologyCategory: TechnologyCategory[] = [];
			org.technologies.forEach((t) => t.technologyCategories.forEach((tc) => tecnologyCategory.push(tc)));
			tecnologyCategory = new List(tecnologyCategory).DistinctBy((key) => key.id).ToArray();
			return new List(tecnologyCategory).Select((a) => a.id).Intersect(new List(this.technologyCategories).Where((t) => t.active).Select((t) => t.value.id)).ToArray().length !== 0;
		}).ToArray();
	}

}



// WEBPACK FOOTER //
// ./Frontend/src/app/components/organizations/organizations.component.ts